import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";

interface Props {
  width: string | number;
  children: React.ReactNode;
}

/**
 * PreviewFrame renders its children inside an iframe so that CSS media queries
 * (used by Material-UI breakpoints) are evaluated against the iframe width
 * instead of the parent window width. This allows us to dynamically preview
 * mobile / tablet / desktop layouts by simply changing the iframe width.
 */
const PreviewFrame = ({ width, children }: Props) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [renderTarget, setRenderTarget] = useState<{
    doc: Document;
    emotionCache: any;
  } | null>(null);

  useLayoutEffect(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || null;

    if (!iframe || !doc) {
      return () => {}; // ensure consistent return type
    }

    doc.open();
    doc.write(
      '<!DOCTYPE html><html><head><style>html{box-sizing:border-box}*,*:before,*:after{box-sizing:inherit}</style></head><body style="margin:0"></body></html>',
    );
    doc.close();

    const emotionCache = createCache({ key: "preview", container: doc.head });
    setRenderTarget({ doc, emotionCache });

    iframe.style.width = typeof width === "number" ? `${width}px` : width;

    return () => {
      doc.open();
      doc.write("");
      doc.close();
    };
  }, [width]);

  // Memoize iframe style to prevent unnecessary re-renders
  const iframeStyle = useMemo(
    () => ({
      width: typeof width === "number" ? `${width}px` : width,
      border: "1px solid #ddd",
      borderRadius: 8,
      transition: "width 0.5s ease",
      height: "100%",
    }),
    [width],
  );

  return (
    <iframe title="website-preview-frame" ref={iframeRef} style={iframeStyle}>
      {renderTarget &&
        ReactDOM.createPortal(
          <CacheProvider value={renderTarget.emotionCache}>
            <style>{`body { font-family: 'Inter', sans-serif; }`}</style>
            {children}
          </CacheProvider>,
          renderTarget.doc.body,
        )}
    </iframe>
  );
};

export default PreviewFrame;
